import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResourcePreview } from '@/components/admin/ResourcePreview';
import { ResourceStats } from '@/components/admin/ResourceStats';
import { Loader2, Sparkles, CheckCircle2, XCircle } from 'lucide-react';

const ResourceGenerator: React.FC = () => {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({ total: 0, processed: 0, successful: 0, failed: 0 });
  const [currentBatch, setCurrentBatch] = useState<any[]>([]);
  const [generatedResources, setGeneratedResources] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('azure');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data: modules } = await supabase
        .from('module_content')
        .select('module_id, course_id, further_reading')
        .order('created_at', { ascending: false });

      if (modules) {
        const total = modules.length;
        
        // Count modules with valid resources (must be non-empty array)
        const withResources = modules.filter(m => {
          return m.further_reading && 
                 Array.isArray(m.further_reading) && 
                 m.further_reading.length > 0;
        }).length;

        setStats({
          total,
          processed: withResources,
          successful: withResources,
          failed: 0
        });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load statistics');
    }
  };

  const generateResourcesForCategory = async (category: string) => {
    setGenerating(true);
    setProgress(0);
    setStats({ total: 0, processed: 0, successful: 0, failed: 0 });
    setGeneratedResources([]);

    try {
      const { data: courses } = await supabase
        .from('courses')
        .select('id, category')
        .eq('category', category);

      if (!courses || courses.length === 0) {
        toast.error(`No courses found for category: ${category}`);
        return;
      }

      const courseIds = courses.map(c => c.id);

      const { data: modules } = await supabase
        .from('module_content')
        .select('module_id, course_id, content, further_reading')
        .in('course_id', courseIds);

      if (!modules || modules.length === 0) {
        toast.error('No modules found for this category');
        return;
      }

      const modulesNeedingResources = modules.filter(m => {
        if (!m.further_reading) return true; // null
        if (!Array.isArray(m.further_reading)) return true; // {} empty object
        return m.further_reading.length === 0; // [] empty array
      });

      const totalModules = modulesNeedingResources.length;
      setStats(prev => ({ ...prev, total: totalModules }));

      toast.info(`Starting generation for ${totalModules} ${category} modules...`);

      const { data: courseModules } = await supabase
        .from('course_modules')
        .select('module_id, module_title, course_id')
        .in('course_id', courseIds);

      const moduleMap = new Map(
        courseModules?.map(m => [m.module_id, m.module_title]) || []
      );

      const BATCH_SIZE = 5;
      let processed = 0;

      for (let i = 0; i < modulesNeedingResources.length; i += BATCH_SIZE) {
        const batch = modulesNeedingResources.slice(i, i + BATCH_SIZE);
        setCurrentBatch(batch);

        const batchPromises = batch.map(async (module) => {
          try {
            const moduleTitle = moduleMap.get(module.module_id) || 'Untitled Module';
            
            const { data, error } = await supabase.functions.invoke('generate-module-resources', {
              body: {
                moduleId: module.module_id,
                courseId: module.course_id,
                moduleTitle,
                moduleContent: module.content,
                category
              }
            });

            if (error) throw error;

            if (data?.success) {
              setGeneratedResources(prev => [...prev, {
                moduleId: module.module_id,
                courseId: module.course_id,
                moduleTitle,
                resources: data.resources
              }]);
              return { success: true };
            }
            return { success: false, error: data?.error };
          } catch (error) {
            console.error(`Failed for module ${module.module_id}:`, error);
            return { success: false, error };
          }
        });

        const results = await Promise.all(batchPromises);
        
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        processed += batch.length;
        setProgress((processed / totalModules) * 100);
        setStats(prev => ({
          ...prev,
          processed,
          successful: prev.successful + successful,
          failed: prev.failed + failed
        }));

        if (i + BATCH_SIZE < modulesNeedingResources.length) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      toast.success(`Completed! Generated resources for ${stats.successful} modules.`);
      await loadStats();

    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate resources');
    } finally {
      setGenerating(false);
      setCurrentBatch([]);
    }
  };

  // PHASE 1: Generate ALL pending modules across all categories
  const generateAllPendingResources = async () => {
    setGenerating(true);
    setProgress(0);
    setStats({ total: 0, processed: 0, successful: 0, failed: 0 });
    setGeneratedResources([]);

      try {
        // Get modules without resources (null, [], or {})
        const { data: modules, error: fetchError } = await supabase
          .from('module_content')
          .select('module_id, course_id, content, further_reading')
          .or('further_reading.is.null,further_reading.eq.[],further_reading.eq.{}');

        if (fetchError) throw fetchError;

      if (!modules || modules.length === 0) {
        toast.success('All modules already have resources!');
        return;
      }

      const totalModules = modules.length;
      setStats(prev => ({ ...prev, total: totalModules }));

      toast.info(`Starting generation for ${totalModules} modules across all categories...`);

      const moduleIds = modules.map(m => m.module_id);
      const { data: courseModules } = await supabase
        .from('course_modules')
        .select('module_id, module_title, course_id')
        .in('module_id', moduleIds);

      // Get course categories
      const courseIds = [...new Set(modules.map(m => m.course_id))];
      const { data: courses } = await supabase
        .from('courses')
        .select('id, category')
        .in('id', courseIds);

      const moduleMap = new Map(
        courseModules?.map(m => [m.module_id, m.module_title]) || []
      );

      const categoryMap = new Map(
        courses?.map(c => [c.id, c.category]) || []
      );

      const BATCH_SIZE = 3;
      let processed = 0;
      let successful = 0;
      let failed = 0;

      for (let i = 0; i < modules.length; i += BATCH_SIZE) {
        const batch = modules.slice(i, i + BATCH_SIZE);
        setCurrentBatch(batch);

        const batchPromises = batch.map(async (module) => {
          try {
            const moduleTitle = moduleMap.get(module.module_id) || 'Untitled Module';
            const category = categoryMap.get(module.course_id) || 'general';
            
            const { data, error } = await supabase.functions.invoke('generate-module-resources', {
              body: {
                moduleId: module.module_id,
                courseId: module.course_id,
                moduleTitle,
                moduleContent: module.content,
                category
              }
            });

            if (error) throw error;

            if (data?.success) {
              setGeneratedResources(prev => [...prev, {
                moduleId: module.module_id,
                courseId: module.course_id,
                moduleTitle,
                resources: data.resources,
                category
              }]);
              return { success: true };
            }
            return { success: false, error: data?.error };
          } catch (error) {
            console.error(`Failed for module ${module.module_id}:`, error);
            return { success: false, error };
          }
        });

        const results = await Promise.all(batchPromises);
        
        successful += results.filter(r => r.success).length;
        failed += results.filter(r => !r.success).length;
        processed += batch.length;

        setProgress((processed / totalModules) * 100);
        setStats({
          total: totalModules,
          processed,
          successful,
          failed
        });

        if (i + BATCH_SIZE < modules.length) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }

      toast.success(`✅ Completed! Generated resources for ${successful} modules. ${failed} failed.`);
      await loadStats();

    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate resources');
    } finally {
      setGenerating(false);
      setCurrentBatch([]);
    }
  };

  // PHASE 5: Retry failed modules
  const retryFailedModules = async () => {
    setGenerating(true);
    setProgress(0);
    setGeneratedResources([]);

      try {
        // Get modules without resources (limit 100 for retry)
        const { data: failedModules, error: fetchError } = await supabase
          .from('module_content')
          .select('module_id, course_id, content, further_reading')
          .or('further_reading.is.null,further_reading.eq.[],further_reading.eq.{}')
          .limit(100);

        if (fetchError) throw fetchError;

      if (!failedModules || failedModules.length === 0) {
        toast.success('No failed modules to retry!');
        setGenerating(false);
        return;
      }

      toast.info(`Retrying ${failedModules.length} failed modules...`);
      
      const moduleIds = failedModules.map(m => m.module_id);
      const { data: courseModules } = await supabase
        .from('course_modules')
        .select('module_id, module_title, course_id')
        .in('module_id', moduleIds);

      // Get course categories
      const courseIds = [...new Set(failedModules.map(m => m.course_id))];
      const { data: courses } = await supabase
        .from('courses')
        .select('id, category')
        .in('id', courseIds);

      const moduleMap = new Map(
        courseModules?.map(m => [m.module_id, m.module_title]) || []
      );

      const categoryMap = new Map(
        courses?.map(c => [c.id, c.category]) || []
      );

      const totalModules = failedModules.length;
      setStats({ total: totalModules, processed: 0, successful: 0, failed: 0 });

      const BATCH_SIZE = 3;
      let processed = 0;
      let successful = 0;
      let failed = 0;

      for (let i = 0; i < failedModules.length; i += BATCH_SIZE) {
        const batch = failedModules.slice(i, i + BATCH_SIZE);
        setCurrentBatch(batch);

        const batchPromises = batch.map(async (module) => {
          try {
            const moduleTitle = moduleMap.get(module.module_id) || 'Untitled Module';
            const category = categoryMap.get(module.course_id) || 'general';
            
            const { data, error } = await supabase.functions.invoke('generate-module-resources', {
              body: {
                moduleId: module.module_id,
                courseId: module.course_id,
                moduleTitle,
                moduleContent: module.content,
                category
              }
            });

            if (error) throw error;

            if (data?.success) {
              setGeneratedResources(prev => [...prev, {
                moduleId: module.module_id,
                courseId: module.course_id,
                moduleTitle,
                resources: data.resources,
                category
              }]);
              return { success: true };
            }
            return { success: false, error: data?.error };
          } catch (error) {
            console.error(`Failed for module ${module.module_id}:`, error);
            return { success: false, error };
          }
        });

        const results = await Promise.all(batchPromises);
        
        successful += results.filter(r => r.success).length;
        failed += results.filter(r => !r.success).length;
        processed += batch.length;

        setProgress((processed / totalModules) * 100);
        setStats({
          total: totalModules,
          processed,
          successful,
          failed
        });

        if (i + BATCH_SIZE < failedModules.length) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }

      toast.success(`✅ Retry completed! Fixed ${successful} modules. ${failed} still failed.`);
      await loadStats();

    } catch (error) {
      console.error('Retry error:', error);
      toast.error('Failed to retry modules');
    } finally {
      setGenerating(false);
      setCurrentBatch([]);
    }
  };

  const categories = [
    { id: 'azure', name: 'Azure', count: 18 },
    { id: 'react', name: 'React', count: 120 },
    { id: 'sql', name: 'SQL', count: 45 },
    { id: 'python', name: 'Python', count: 80 },
    { id: 'ai', name: 'AI/ML', count: 35 }
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            AI Resource Generator
          </h1>
          <p className="text-muted-foreground mt-2">
            Automatically generate educational resources for course modules using AI
          </p>
        </div>
      </div>

      <ResourceStats stats={stats} />

      <Tabs defaultValue="generate" className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">Generate Resources</TabsTrigger>
          <TabsTrigger value="preview">Preview & Edit</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-4">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Select Category</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {categories.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="h-auto py-4 flex flex-col items-start"
                  disabled={generating}
                >
                  <span className="font-semibold">{cat.name}</span>
                  <span className="text-sm text-muted-foreground">~{cat.count} modules</span>
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={() => generateResourcesForCategory(selectedCategory)}
                disabled={generating}
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Generate {categories.find(c => c.id === selectedCategory)?.name}
                  </>
                )}
              </Button>

              <Button
                onClick={generateAllPendingResources}
                disabled={generating}
                size="lg"
                variant="default"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating All...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    🚀 Generate ALL Pending
                  </>
                )}
              </Button>

              <Button
                onClick={retryFailedModules}
                disabled={generating}
                size="lg"
                variant="outline"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    🔄 Retry Failed
                  </>
                )}
              </Button>
            </div>

            {generating && (
              <div className="mt-6 space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progress</span>
                    <span>{stats.processed} / {stats.total}</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{stats.processed}</div>
                    <div className="text-xs text-muted-foreground">Processed</div>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-5 w-5" />
                      {stats.successful}
                    </div>
                    <div className="text-xs text-muted-foreground">Successful</div>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-red-600 flex items-center justify-center gap-1">
                      <XCircle className="h-5 w-5" />
                      {stats.failed}
                    </div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                </div>

                {currentBatch.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    Processing batch of {currentBatch.length} modules...
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="preview">
          <ResourcePreview 
            resources={generatedResources}
            onRefresh={loadStats}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ResourceGenerator;
